import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';
import { useAuth } from '../lib/auth';

const DOMAINS = [
  { id: 'patient_care',      title: 'Patient Care / Clinical Skills' },
  { id: 'medical_knowledge', title: 'Medical Knowledge' },
  { id: 'communication',     title: 'Interpersonal & Communication Skills' },
  { id: 'practice_learning', title: 'Practice-Based Learning Skills' },
  { id: 'system_practice',   title: 'System-Based Practice Skills' },
  { id: 'professionalism',   title: 'Professionalism' },
];

const scoreLabel = (n) => {
  if (!n) return '—';
  if (n <= 4) return 'Unsatisfactory';
  if (n <= 6) return 'Satisfactory';
  if (n <= 8) return 'Good';
  return 'Outstanding';
};

const scoreColor = (n) => {
  if (!n) return '#888';
  if (n <= 4) return '#E85252';
  if (n <= 6) return '#F4A83A';
  if (n <= 8) return '#4A9D6F';
  return '#1A3A5C';
};

export default function FellowEvaluations({ isMobile }) {
  const { user, profile } = useAuth();
  const [evaluations, setEvaluations] = useState([]);
  const [selected, setSelected]       = useState(null);
  const [fellowComments, setFellowComments] = useState('');
  const [acknowledging, setAcknowledging]   = useState(false);
  const [loading, setLoading]         = useState(true);

  useEffect(() => { fetchEvals(); }, []);

  const fetchEvals = async () => {
    setLoading(true);
    const { data } = await supabase.from('evaluations').select('*')
      .eq('fellow_id', user.id).order('created_at', { ascending: false });
    setEvaluations(data || []);
    setLoading(false);
  };

  const acknowledge = async () => {
    setAcknowledging(true);
    await supabase.from('evaluations').update({
      acknowledgment_status: 'acknowledged',
      acknowledged_at: new Date().toISOString(),
      fellow_comments: fellowComments,
    }).eq('id', selected.id);
    setAcknowledging(false);
    fetchEvals();
    setSelected(null);
    setFellowComments('');
  };

  const pendingCount = evaluations.filter(e => e.acknowledgment_status === 'pending').length;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading evaluations…</div>;

  if (selected) {
    const e = selected;
    const totalScore = e.total_score || DOMAINS.reduce((s, d) => s + (e[`score_${d.id}`] || 0), 0);
    const pct = Math.round((totalScore / 60) * 100);
    const isPending = e.acknowledgment_status === 'pending';
    return (
      <div style={{ background: '#F7F4F0', minHeight: '100vh', paddingBottom: isMobile ? 80 : 40 }}>
        <div style={{ background: '#1A3A5C', padding: isMobile ? '14px 16px' : '16px 40px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#7EB8D4', fontSize: 14, cursor: 'pointer', padding: 0 }}>← Back</button>
          <span style={{ fontFamily: "'DM Serif Display'", fontSize: isMobile ? 16 : 20, color: '#F5F0E8' }}>My Evaluation</span>
        </div>

        <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? 16 : '28px 40px' }}>
          {/* Header card */}
          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', borderTop: '4px solid #1A3A5C' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Academic Affairs Office · Physician Education Center</div>
              <div style={{ fontFamily: "'DM Serif Display'", fontSize: 18, color: '#1A3A5C' }}>Physician Trainee General Evaluation Form</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Department of Hospice and Palliative Medicine @ KHCC</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, borderTop: '1px solid #F0EDE8', paddingTop: 16 }}>
              {[
                { label: 'Trainee', val: e.fellow_name },
                { label: 'Attending', val: e.evaluator_name },
                { label: 'Rotation Period', val: e.rotation_period || '—' },
                { label: 'Evaluation Date', val: e.evaluation_date },
              ].map((row, i) => (
                <div key={i}>
                  <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{row.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1F' }}>{row.val}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Domain scores */}
          {DOMAINS.map((domain, idx) => {
            const score = e[`score_${domain.id}`];
            const comment = e[`comment_${domain.id}`];
            return (
              <div key={domain.id} style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'DM Serif Display'", fontSize: 15, color: '#1A3A5C', marginBottom: 4 }}>{idx + 1}. {domain.title}</div>
                  {comment && <div style={{ fontSize: 13, color: '#666', fontStyle: 'italic', marginTop: 4 }}>"{comment}"</div>}
                </div>
                <div style={{ textAlign: 'center', minWidth: 72 }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: scoreColor(score), display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px' }}>
                    <span style={{ fontFamily: "'DM Serif Display'", fontSize: 22, color: '#fff', fontWeight: 700 }}>{score}</span>
                  </div>
                  <div style={{ fontSize: 10, color: scoreColor(score), fontWeight: 600 }}>{scoreLabel(score)}</div>
                </div>
              </div>
            );
          })}

          {/* Total */}
          <div style={{ background: '#1A3A5C', borderRadius: 12, padding: '20px 28px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#7EB8D4', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Total Score</div>
              <div style={{ fontFamily: "'DM Serif Display'", fontSize: 44, color: '#F5F0E8', lineHeight: 1 }}>{totalScore}<span style={{ fontSize: 20, opacity: 0.5 }}>/60</span></div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#7EB8D4', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Percentage</div>
              <div style={{ fontFamily: "'DM Serif Display'", fontSize: 44, color: '#F5F0E8', lineHeight: 1 }}>{pct}<span style={{ fontSize: 20, opacity: 0.5 }}>%</span></div>
              <div style={{ fontSize: 13, color: pct >= 90 ? '#7EB8D4' : pct >= 70 ? '#A8DCC0' : pct >= 50 ? '#F4DFA8' : '#F4A8A8', marginTop: 4, fontWeight: 600 }}>
                {pct >= 90 ? '★ Outstanding' : pct >= 70 ? '✓ Good' : pct >= 50 ? '~ Satisfactory' : '✗ Unsatisfactory'}
              </div>
            </div>
          </div>

          {/* Acknowledgment */}
          {isPending ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <div style={{ fontFamily: "'DM Serif Display'", fontSize: 16, color: '#1A1A1F', marginBottom: 8 }}>Trainee Acknowledgment</div>
              <p style={{ fontSize: 13, color: '#666', lineHeight: 1.7, marginBottom: 14 }}>
                By acknowledging this evaluation, you confirm that you have read and understood its contents. You may add your comments below before signing.
              </p>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Your Comments (optional)</label>
              <textarea value={fellowComments} rows={3}
                placeholder="Add any comments or feedback about this evaluation…"
                onChange={e => setFellowComments(e.target.value)}
                style={{ width: '100%', padding: '10px 13px', border: '1.5px solid #E0DDD8', borderRadius: 8, fontSize: 13, fontFamily: "'DM Sans'", resize: 'vertical', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }} />
              <button onClick={acknowledge} disabled={acknowledging}
                style={{ width: '100%', padding: '14px', background: '#1E5C40', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: acknowledging ? 0.7 : 1, fontFamily: "'DM Sans'" }}>
                {acknowledging ? 'Processing…' : '✓ Acknowledge & Sign'}
              </button>
            </div>
          ) : (
            <div style={{ background: '#F0FFF6', border: '1px solid #A8DCC0', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1E5C40', marginBottom: 4 }}>✓ Acknowledged</div>
              <div style={{ fontSize: 13, color: '#555' }}>Signed on {new Date(e.acknowledged_at).toLocaleDateString()}</div>
              {e.fellow_comments && <div style={{ fontSize: 13, color: '#555', marginTop: 8, fontStyle: 'italic' }}>Your comments: "{e.fellow_comments}"</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : '36px 40px', paddingBottom: isMobile ? 80 : 40 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <h1 style={{ fontFamily: "'DM Serif Display'", fontSize: isMobile ? 22 : 28, color: '#1A1A1F' }}>My Evaluations</h1>
        {pendingCount > 0 && (
          <div style={{ background: '#FFF0E8', border: '1px solid #F4C8A8', color: '#C4622A', padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
            {pendingCount} pending acknowledgment
          </div>
        )}
      </div>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>{evaluations.length} evaluation{evaluations.length !== 1 ? 's' : ''} received</p>

      {evaluations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 15 }}>No evaluations yet</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Your evaluations will appear here when submitted by faculty</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {evaluations.map(e => {
            const totalScore = e.total_score || 0;
            const pct = Math.round((totalScore / 60) * 100);
            const isPending = e.acknowledgment_status === 'pending';
            return (
              <div key={e.id} onClick={() => setSelected(e)}
                style={{ background: '#fff', borderRadius: 12, padding: isMobile ? '14px' : '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', cursor: 'pointer', borderLeft: `4px solid ${isPending ? '#C4622A' : '#1E5C40'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#1A3A5C' }}>Evaluated by {e.evaluator_name}</span>
                      <span style={{ fontSize: 11, color: '#999' }}>{e.evaluation_date}</span>
                    </div>
                    {e.rotation_period && <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Rotation: {e.rotation_period}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: isPending ? '#FFF8EE' : '#F0FFF6', color: isPending ? '#C4622A' : '#1E5C40', border: `1px solid ${isPending ? '#F4DFA8' : '#A8DCC0'}` }}>
                        {isPending ? '⏳ Pending acknowledgment' : '✓ Acknowledged'}
                      </span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', marginLeft: 16 }}>
                    <div style={{ fontFamily: "'DM Serif Display'", fontSize: 28, color: scoreColor(Math.round(totalScore / 6)), lineHeight: 1 }}>{pct}%</div>
                    <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{totalScore}/60</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
